import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import type { BusinessSearchResponse } from '@/types';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '1');

  const [input, setInput] = useState(initialQuery);

  useEffect(() => {
    setInput(initialQuery);
  }, [initialQuery]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['business-search', initialQuery, page],
    queryFn: () =>
      apiClient<BusinessSearchResponse>('/public/businesses/search', {
        params: { q: initialQuery, page, page_size: 20 },
      }),
    staleTime: 60_000,
    retry: 1,
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (input.trim()) params.q = input.trim();
    params.page = '1';
    setSearchParams(params);
  };

  const totalPages = data ? Math.ceil(data.meta.total / data.meta.page_size) : 0;

  return (
    <div className="container-page py-10">
      <h1 className="text-3xl font-bold text-slate-900">Search businesses</h1>
      <p className="mt-2 text-slate-600">Find local shops and services across India</p>

      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-3 sm:flex-row">
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Business name, product, city..."
          className="flex-1 rounded-brand border border-slate-300 px-4 py-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
          aria-label="Search query"
        />
        <Button type="submit">Search</Button>
      </form>

      <div className="mt-10">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
          </div>
        )}

        {isError && (
          <div className="rounded-brand border border-red-200 bg-red-50 p-6 text-center text-red-700">
            {(error as Error).message || 'Search failed. Please try again.'}
          </div>
        )}

        {data && !isLoading && (
          <>
            <p className="text-sm text-slate-500">
              {data.meta.total} result{data.meta.total !== 1 ? 's' : ''}
              {initialQuery ? ` for "${initialQuery}"` : ''}
            </p>

            {data.data.length === 0 ? (
              <div className="mt-8 rounded-brand border border-slate-200 bg-white p-12 text-center">
                <p className="text-lg font-medium text-slate-700">No businesses found</p>
                <p className="mt-2 text-sm text-slate-500">Try a different search term or browse categories.</p>
                <Link to="/" className="mt-4 inline-block text-sm font-medium text-brand-primary hover:underline">
                  Back to home
                </Link>
              </div>
            ) : (
              <ul className="mt-6 divide-y divide-slate-200 rounded-brand border border-slate-200 bg-white">
                {data.data.map((business) => (
                  <li key={business.id}>
                    <Link
                      to={
                        business.state_slug && business.city_slug
                          ? `/india/${business.state_slug}/${business.city_slug}/${business.slug}`
                          : `/search?q=${encodeURIComponent(business.name)}`
                      }
                      className="flex items-center justify-between gap-4 px-6 py-5 transition hover:bg-slate-50"
                    >
                      <div>
                        <h2 className="font-semibold text-slate-900">{business.name}</h2>
                        {business.category && (
                          <p className="mt-1 text-sm text-slate-500">{business.category}</p>
                        )}
                      </div>
                      <span className="shrink-0 text-sm font-medium text-amber-600">
                        ★ {business.rating_avg.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    const params = Object.fromEntries(searchParams);
                    params.page = String(page - 1);
                    setSearchParams(params);
                  }}
                >
                  Previous
                </Button>
                <span className="px-3 text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    const params = Object.fromEntries(searchParams);
                    params.page = String(page + 1);
                    setSearchParams(params);
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
